// The money-free per-artist summary cards — the Music / Marketing / Content / To-dos cards + the
// Card/Line primitives, EXTRACTED so BOTH the Label per-artist dashboard (RosterArtistDashboard) and the
// Management per-artist heartbeat (RosterArtistHeartbeat) render the SAME cards from the SAME composed read
// (`RosterArtistDashboardRead`). MONEY-FREE by construction: the DTO carries no money field; each card is a
// summary of the SAME projections the deep tabs read. Born-blank fields render '—'.
import { useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { Card, Icon, StatDelta, compactNumber } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useNeedsAttention } from "../../app/hooks/useNeedsAttention.js";
import { useMe } from "../../app/hooks/useSession.js";
import { useDirectoryPeople } from "../tasks/taskPeople.js";
import { TaskListRow } from "../tasks/TaskListRow.js";
import { asTaskSummary, sortTasksForList, taskRowsClassName } from "../tasks/taskRow.js";
import { TaskCreateModal } from "../tasks/TaskCreateModal.js";
import { NeedsAttentionCard } from "./NeedsAttentionCard.js";
import { TrendingPillList, TrendingTitle } from "../dashboard/Trending.js";
import { NewDealModal } from "./NewDealModal.js";
import { openRequests, opportunityRow, showRequestRow } from "../dashboard/oppCompact.js";
import { oppLeftFeed } from "../dashboard/oppDisplay.js";
import { useShowRequests } from "../../app/hooks/useShowRequests.js";
import { ShowRequestDetails } from "../booking/ShowRequestDetails.js";
import { useCapability } from "../../app/bandContext.js";
import { OppCompactRow } from "../dashboard/OppCompactRow.js";
import { CalendarEventDetails, type OpenCalendarEvent } from "../booking/CalendarEventDetails.js";
import styles from "./RosterArtist.module.scss";

type RosterMusicCard = components["schemas"]["RosterMusicCard"];
type RosterMarketingCard = components["schemas"]["RosterMarketingCard"];
type RosterTodosCard = components["schemas"]["RosterTodosCard"];
type TrendingItem = components["schemas"]["TrendingItem"];
type OpportunityDto = components["schemas"]["OpportunityDto"];

// Trending and Opportunities render on the SHARED `@cntrl/ui` Card. They used to have a local `Card` of
// their own here, and it was a third hand-rolled copy of that primitive carrying the third instance of the
// same drift (ART-115): 16px padding against the primitive's 24, a 16px radius against its 24, and a 14px
// `fw-bold` title against its 16px `fw-black` — so on the Booking dashboard the Opportunities header sat
// 8px left of and a size below every card around it, and on the Label dashboard it did the same beside the
// `.summary` cards, whose shell had always matched the primitive.
//
// Its title was also a <span>, not a heading, so "Opportunities" was in NO document outline anywhere — the
// one card on three dashboards a screen-reader user could not jump to, and the reason it was the only
// section on the page that a `getByRole("heading")` sweep could not see. That is the tell this whole family
// leaves: a hand-rolled panel copies the LOOK of a primitive and silently drops its semantics.
//
// Nothing else imported that component, and its "Open tab →" prop had no caller at all — `SummaryCard` owns
// that affordance — so it went whole rather than being reimplemented over the primitive.

/**
 * The SUMMARY CARD — the shape Music, Marketing and Content all take: the title with its
 * deep-tab link on the same line, then one LEAD figure over hairline-separated rows.
 *
 * One component for the three because legacy's is one (`SummaryCardView`, fed a `SummaryCard` record). The
 * lead is a slot rather than a number: Marketing's headline is the active release's TITLE, so a card that
 * only accepted a figure would have forced that lens to fork the whole card.
 */
export function SummaryCard({ title, openLabel, onOpen, lead, leadLabel, children }: {
  title: string;
  openLabel: string;
  onOpen?: () => void;
  /** The headline. `null` renders the em dash in the muted tone — never a fabricated 0. */
  lead?: ReactNode;
  /** Omit BOTH this and `lead` for a card with no headline (To-do) — the block is skipped, not blanked. */
  leadLabel?: string;
  children: ReactNode;
}) {
  const empty = lead == null || lead === "";
  return (
    <section className={styles.summary} aria-label={title}>
      {/* The title and its deep-tab link on one line (ART-334/332) — the source eyebrow that sat above them went. */}
      <header className={styles.summaryHead}>
        <h3 className={styles.summaryTitle}>{title}</h3>
        {onOpen && (
          <button type="button" className={styles.summaryOpen} onClick={onOpen}>{openLabel} →</button>
        )}
      </header>
      {leadLabel && (
        <div className={styles.summaryLead}>
          <span className={styles.summaryLeadValue} data-empty={empty || undefined}>{empty ? "—" : lead}</span>
          <span className={styles.summaryLeadLabel}>{leadLabel}</span>
        </div>
      )}
      <div className={styles.summaryRows}>{children}</div>
    </section>
  );
}

/**
 * One row of a summary card: a mono label, the value, an optional delta chip, and an optional sub-line
 * under it. `value` is a NODE, not a number, because a row can carry a track title as readily as a count —
 * so each caller formats its own and this owns only the layout.
 */
export function SummaryRow({ label, value, delta, sub }: {
  label: string;
  value: ReactNode;
  delta?: number | null;
  sub?: ReactNode;
}) {
  const empty = value == null || value === "";
  return (
    <div className={styles.summaryRow}>
      <span className={styles.summaryRowLabel}>{label}</span>
      <span className={styles.summaryRowValue} data-empty={empty || undefined}>
        {empty ? "—" : value}
        {!empty && delta != null && <StatDelta value={delta} className={styles.summaryRowDelta} />}
      </span>
      {sub && <span className={styles.summaryRowSub}>{sub}</span>}
    </div>
  );
}

/**
 * Money for a summary row. Null → '—', never a fabricated $0: a cycle with no recorded spend and a cycle we
 * hold no figure for are both "we cannot tell you", and $0 is a claim.
 */
function usd(cents: number | null | undefined): ReactNode {
  if (cents == null || !Number.isFinite(cents)) return null;
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

/** A count for a summary row: null → '—' (the born-blank contract), otherwise the compact form. */
function num(value: number | null | undefined): ReactNode {
  return value != null && Number.isFinite(value) ? compactNumber(value) : null;
}

/** One label/value line inside a card (null value → '—', the born-blank contract). */
export function Line({ label, value }: { label: string; value: number | null | undefined }) {
  const has = value != null && Number.isFinite(value);
  return (
    <div className={styles.miniRow}>
      <span className={styles.miniLabel}>{label}</span>
      <span className={styles.miniValue}>{has ? compactNumber(value as number) : "—"}</span>
    </div>
  );
}

/**
 * The Music card — legacy's five rows, in its order. The lead repeats the monthly-listeners row on purpose:
 * legacy promotes that figure to the headline and keeps the row (it only de-duplicates Marketing's active
 * release and Content's total reach, which have no delta of their own to show).
 */
export function MusicCard({ music, onOpen }: { music: RosterMusicCard | undefined; onOpen?: () => void }) {
  return (
    <SummaryCard
      title="Music"
      openLabel="Open Music"
      onOpen={onOpen}
      lead={num(music?.monthlyListeners)}
      leadLabel="Spotify monthly listeners"
    >
      <SummaryRow
        label="Spotify monthly listeners"
        value={num(music?.monthlyListeners)}
        delta={music?.monthlyListenersDeltaPct}
      />
      <SummaryRow label="Streams · 28d" value={num(music?.streams28d)} />
      <SummaryRow label="Radio spins · total" value={num(music?.radioSpinsTotal)} />
      <SummaryRow
        label="Top track · 28d"
        value={music?.topTrackTitle}
        // The streams that earned it the slot. Without them the row names a track and says nothing about it.
        sub={music?.topTrackStreams28d != null ? `+${compactNumber(music.topTrackStreams28d)} streams` : undefined}
      />
      <SummaryRow label="Charts current" value={num(music?.chartsCurrent)} />
    </SummaryCard>
  );
}

/**
 * The Marketing card. The active release is the HEADLINE (legacy promotes it and drops the duplicate row),
 * and the rollout row is formatted here from the chip — the client owns the words, the server owns the days.
 *
 * Legacy's "Ad spend · cycle" row is deliberately absent: it is cents on a money-free seat. It costs nothing
 * to omit, because legacy's own row is a permanent em dash (see RosterMarketingCard).
 */
export function MarketingCard({ marketing, onOpen }: { marketing: RosterMarketingCard | undefined; onOpen?: () => void }) {
  const campaign = marketing?.campaign;
  const rollout =
    campaign?.daysToRelease != null
      ? `Upcoming · ${campaign.daysToRelease} days out`
      : campaign?.dayInCycle != null
        ? `In cycle · day ${campaign.dayInCycle}`
        : null;
  return (
    <SummaryCard
      title="Marketing"
      openLabel="Open Marketing"
      onOpen={onOpen}
      lead={campaign?.title}
      leadLabel="Active release"
    >
      {/* MTD, and the label says so — our only projection of this is month-scoped, and legacy's "LIFETIME"
          heading over it would be a real number under the wrong window. */}
      <SummaryRow label="Smart-Link · MTD" value={num(marketing?.smartLinkClicksMtd)} />
      {/* MONEY, and the only money on this page. It renders when the server sends the BLOCK — absent means
          this seat is not entitled to the artist's marketing money, which must read as no row at all rather
          than as the em dash an entitled seat with no figure gets. The block is ungated server-side today
          (the per-lens money roles are being built), so every seat currently sees the row. */}
      {marketing?.adSpend && (
        <SummaryRow label="Ad spend · cycle" value={usd(marketing.adSpend.cycleCents)} />
      )}
      <SummaryRow label="Active ads" value={num(marketing?.activeAds)} />
      <SummaryRow label="Rollout" value={rollout} />
    </SummaryCard>
  );
}

/**
 * The red "+" in a card's head (product owner, ART-324: "+ buttons are red"). One component so the To-do and
 * Opportunities cards — and any card that grows one later — draw the same control.
 */
export function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={styles.addButton} aria-label={label} title={label} onClick={onClick}>
      <Icon icon={Plus} size={16} />
    </button>
  );
}

/**
 * The To-do card (ART-324) — one of the three cards in the artist page's top row, on every lens.
 *
 * A row opens the routed task drawer at `?task=:id` (ART-119), and shows who it is FROM and who it is ON as the
 * artist side's faces (ART-333) — a seat manager reads the org's whole work on the artist here, not only their own
 * (ART-122), so "who asked whom" is half of what the row means. Faces come from the payload's own `people`
 * directory (the only source that can name an org colleague, who holds no band seat).
 *
 * `onAdd` draws the red + (the shared create modal, scoped to this artist).
 */
export function TodosCard({
  todos,
  onOpenTask,
  onAdd,
}: {
  todos: RosterTodosCard | null | undefined;
  onOpenTask?: (taskId: string) => void;
  onAdd?: () => void;
}) {
  const people = useDirectoryPeople(todos?.people ?? undefined);
  // The artist side's row (ART-333), a size down: the task, then who handed it to whom as faces, then the due date
  // — `TaskListRow` itself, so the two sides cannot drift. The roster line carries ids, not assignment records, so
  // the task's initiator stands in as the assigner (the row's own fallback for a task with no assigner).
  const rows = sortTasksForList((todos?.items ?? []).map(asTaskSummary));
  return (
    <Card
      title="To-do"
      className={styles.triadCard}
      actions={onAdd ? <AddButton label="New task" onClick={onAdd} /> : undefined}
    >
      {rows.length === 0 ? (
        <div className={styles.miniNote}>No open to-dos</div>
      ) : (
        <div className={styles.todoBleed}>
          <ul className={taskRowsClassName(styles.triadList)}>
            {rows.map((t, i) => (
              <TaskListRow
                key={t.taskId ?? i}
                task={t}
                people={people}
                density="compact"
                onOpen={onOpenTask && t.taskId ? () => onOpenTask(t.taskId as string) : undefined}
              />
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/**
 * The Trending card — this artist's live spikes, money-neutral.
 *
 * Fed by `dashboard.trending`, which is the SAME `TrendingBlock.Items` the artist's own dashboard renders:
 * one detection, two surfaces, so an org seat and the band can never be shown different spikes. It is also
 * the gap-checked lane — `Items` refuses a pair wider than the configured max gap, unlike the movers list,
 * which takes the two most recent rows whatever their dates.
 */
export function TrendingCard({ trending, className }: {
  trending: readonly TrendingItem[] | null | undefined;
  className?: string;
}) {
  const items = trending ?? [];
  return (
    <Card title={<TrendingTitle count={items.length} />} className={className}>
      {items.length === 0 ? (
        <div className={styles.miniNote}>No spikes right now</div>
      ) : (
        // The artist dashboard's own pill list. `subject` is a dedupe KEY, not a label — printing it raw is
        // what put "Platform:Tiktok:Followers_total" on this card; `trendingCopy` is the one place an item
        // becomes English, and both surfaces read the same series.
        <TrendingPillList items={items} />
      )}
    </Card>
  );
}

/**
 * The Opportunities card — the ORG-SCOPED feed for this artist, with create.
 *
 * Scoping is the server's, and it is structural: the read runs without `app.opp_auto_view`, so the
 * RESTRICTIVE `opp_visibility` policy leaves only what this viewer created or was invited to. The band's
 * internal pipeline is not filtered out here — it never arrives.
 *
 * CREATE is offered on every lens, label included (PO, roster-opps: "Label/MGMT/Booking all need to have
 * opportunities for each band and the ability to create them"). The money invariant needs no lens prop to
 * hold: `NewDealModal` creates a money-free opportunity and chains the figure as a separate money-gated
 * sub-resource shown only when `canSeeOppMoney`, so a label seat (money:none) never authors a field it
 * cannot read back — the same outcome legacy got by hiding an input on an `orgKind` check, but enforced by
 * the capability rather than remembered at the call site.
 */
export function OpportunitiesCard({
  opportunities,
}: {
  opportunities: readonly OpportunityDto[] | null | undefined;
}) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<OpportunityDto | null>(null);
  const [details, setDetails] = useState<OpenCalendarEvent | null>(null);
  const myPersonId = useMe().data?.person?.personId;
  // ART-338: answered invites and fully-confirmed opportunities of your own have moved to the calendar.
  const items = (opportunities ?? []).filter((o) => !oppLeftFeed(o, myPersonId));
  // ART-327 — show requests waiting on the team, for the people who decide them (Mgmt / the artist AV).
  const decides = useCapability("booking:decide");
  const requests = openRequests(useShowRequests({ enabled: decides }).data?.requests);
  const [request, setRequest] = useState<string | null>(null);
  return (
    <>
      <Card
        title="Opportunities"
        className={styles.triadCard}
        actions={<AddButton label="New opportunity" onClick={() => setCreating(true)} />}
      >
        {items.length === 0 && requests.length === 0 ? (
          <div className={styles.miniNote}>No opportunities yet</div>
        ) : (
          // ART-328 — the compact card: the creator gets Edit, an invitee Details (where they answer).
          <ul className={`${styles.oppCompactList} ${styles.triadList}`}>
            {requests.map((r) => (
              <OppCompactRow
                key={`request:${r.offerId}`}
                {...showRequestRow(r)}
                actionLabel="Details"
                onAction={() => setRequest(r.offerId!)}
              />
            ))}
            {items.map((o, i) => {
              const id = o.opportunityId ?? `opp-${i}`;
              const m = opportunityRow(o, myPersonId);
              return (
                <OppCompactRow
                  key={id}
                  {...m}
                  actionLabel={m.side === "creator" ? "Edit" : "Details"}
                  onAction={() => m.side === "creator"
                    ? setEditing(o)
                    : setDetails({ kind: "opportunity", id, anchor: null })}
                />
              );
            })}
          </ul>
        )}
      </Card>
      {editing && <NewDealModal opp={editing} title="Edit opportunity" onClose={() => setEditing(null)} />}
      {details && <CalendarEventDetails open={details} onClose={() => setDetails(null)} />}
      {request && <ShowRequestDetails offerId={request} onClose={() => setRequest(null)} />}
      {creating && (
        <NewDealModal
          title="New opportunity"
          submitLabel="Create"
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

/**
 * The artist page's top row (ART-324): Needs attention · Opportunities · To-do, the same three cards on the
 * Management, Label and Booking lenses. Needs attention is awareness (it scrolls); the other two are open — anyone
 * on the artist can create into them, hence their red +. The task create is the artist-side modal, scoped to this
 * artist through the mounted RosterBandProvider.
 */
export function ArtistAttentionRow({
  opportunities,
  todos,
  onOpenTask,
  onChanged,
  canOpenShows = false,
  showOpportunities = true,
}: {
  opportunities: readonly OpportunityDto[] | null | undefined;
  todos: RosterTodosCard | null | undefined;
  onOpenTask?: (taskId: string) => void;
  /** Refetch the page's composed read after a create or an edit. */
  onChanged?: () => void;
  /** Hold / show rows get Open (the full show editor) — off for a lens that cannot edit the schedule. */
  canOpenShows?: boolean;
  /** A booking agency has no Opportunities card (product owner, ART-331): Needs attention and To-do only. */
  showOpportunities?: boolean;
}) {
  const attention = useNeedsAttention();
  const [taskOpen, setTaskOpen] = useState(false);
  return (
    <>
      <div className={styles.triad} data-cols={showOpportunities ? undefined : 2}>
        <NeedsAttentionCard
          scope="artist"
          className={styles.triadCard}
          // The red "+" sets the height of its neighbours' heads; an empty slot of its size keeps this card's
          // title and list level with theirs (ART-330).
          actions={<span className={styles.addButtonSpacer} aria-hidden="true" />}
          items={attention.data?.items}
          total={attention.data?.total}
          isLoading={attention.isLoading}
          canOpenShows={canOpenShows}
          onEdited={() => {
            void attention.refetch();
            onChanged?.();
          }}
        />
        {showOpportunities && <OpportunitiesCard opportunities={opportunities} />}
        <TodosCard todos={todos} onOpenTask={onOpenTask} onAdd={() => setTaskOpen(true)} />
      </div>
      {/* Server-confirm then repaint: the modal closes only on a persisted write, the page's composed read is
          refetched so the To-do card shows the new task, and the new task opens in its drawer — the artist side's
          create → drawer flow (ART-343). Passing onCreated means the modal leaves the close to us; not closing
          it left the form up, and a second press created the task twice. */}
      {taskOpen && (
        <TaskCreateModal
          onClose={() => setTaskOpen(false)}
          onCreated={(taskId) => {
            setTaskOpen(false);
            onChanged?.();
            onOpenTask?.(taskId);
          }}
        />
      )}
    </>
  );
}
